// src/pages/Billing/refusalCountRules.ts — the shape of GET /billing/my/refusals and the pure
// helpers the panel uses (docs/BILLING.md §4.10 in the backend repo).
//
// refused     = the switch refused the call (REJECT: prepaid balance at the floor in Enforce, or CatchMyCall)
// wouldRefuse = it would have, but the gate is in log mode or the account is in Soft mode (WOULD_REJECT)
// capped      = the call was allowed with a maximum length (CAP); wouldCap = log mode (WOULD_CAP)

export interface RefusalCounts {
  refused: number;
  wouldRefuse: number;
  capped: number;
  wouldCap: number;
}

export interface RefusalSummary {
  organizationId: string | null;
  organizationName: string | null;
  /** The backend ingest flag; false = the switches are not reporting yet. */
  enabled: boolean;
  days: number;
  from: string;
  to: string;
  totals: RefusalCounts;
  byDay: { day: string; counts: RefusalCounts }[];
  byLookup: { lookup: string; counts: RefusalCounts }[];
  /** null for a business (it is the only one). */
  byOrganization: { organizationId: string | null; organizationName: string; counts: RefusalCounts }[] | null;
}

export const REFUSAL_WINDOWS = [7, 30] as const;

export const LOOKUP_LABEL: Record<string, string> = {
  PREPAID: 'Prepaid balance',
  CMC: 'CatchMyCall subscription',
};

export const totalOf = (c?: RefusalCounts | null): number =>
  c ? Number(c.refused || 0) + Number(c.wouldRefuse || 0) + Number(c.capped || 0) + Number(c.wouldCap || 0) : 0;

/** The one-line headline under the panel title. */
export function refusalHeadline(s: RefusalSummary): string {
  const t = s.totals;
  if (!s.enabled && totalOf(t) === 0) return 'The switches are not reporting refused calls yet.';
  if (totalOf(t) === 0) return `No calls were refused or capped in the last ${s.days} days.`;
  const parts: string[] = [];
  if (t.refused) parts.push(`${t.refused} refused`);
  if (t.wouldRefuse) parts.push(`${t.wouldRefuse} would have been refused`);
  if (t.capped) parts.push(`${t.capped} capped`);
  if (t.wouldCap) parts.push(`${t.wouldCap} would have been capped`);
  return `${parts.join(', ')} in the last ${s.days} days.`;
}

/** Days with something to show, newest first: the full window of zero days is noise. */
export const activeDays = (s: RefusalSummary) =>
  [...s.byDay].filter((d) => totalOf(d.counts) > 0).reverse();
